import { PropsWithChildren, StrictMode, useEffect, useState } from "react";
import { CookiesProvider } from "react-cookie";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from "react-router";
import ProvidersRoot from "@app/ProvidersRoot";
import {
  BtSdkAdsCanaryConfigProvider,
  useBtSdkAdsCanaryConfigContext,
} from "@components/Advertisement/BtSdkAdsCanaryConfigContext";
import BtSdkAdsShell from "@components/Advertisement/BtSdkAdsShell";
import CASBlock from "@components/CASBlock";
import PackageVersionTag from "@components/PackageVersionTag/PackageVersionTag";
import PageGeneratedAtTag from "@components/PageGeneratedAtTag/PageGeneratedAtTag";
import AdvertisementOverlay from "@features/advertisement/components/AdvertisementOverlay";
import ChartbeatBodyScript from "@features/analytics/ChartbeatBodyScript";
import { URL_ORIGIN } from "@features/seo/utils/constants";
import useAllCustomHooks from "@hooks/useAllCustomHooks";
import usePodcastPlayerStyle from "@hooks/usePodcastPlayerStyle";
import ConfigurationSettings from "@particles/configurationSettings/ConfigurationSettings";
import { USFOrgSchema } from "@sphtech/web2-seo-schema";
import { StickyAudioPlayer } from "@sphtech/web2-sticky-audio-player";
import { CDP_EXIT_INTERVENTION_MODULE_ID } from "@util/constant";
import { cn } from "@util/helpers";
import {
  newRelicAgentUrl,
  newRelicConfigScript,
} from "@web2/providers/NewRelic";

import Favicon from "./components/Favicon/Favicon";
import { RouteFactory, routesToExcludeFromLightbox } from "./routePaths";
import { Route } from "../../.react-router/types/src/app/+types/root";

// Order of imports will affect default margins on html elements
import "@styles/index.css";
import "@sphtech/web2-sticky-audio-player/styles.css";

// Wrapper component that sets up the HTML shell and global scripts
export const Layout = ({ children }: PropsWithChildren) => {
  return (
    <BtSdkAdsCanaryConfigProvider>
      <LayoutShell>{children}</LayoutShell>
    </BtSdkAdsCanaryConfigProvider>
  );
};

const LayoutShell = ({ children }: PropsWithChildren) => {
  const sdkAdsConfig = useBtSdkAdsCanaryConfigContext();
  const sdkAdsEnabled = typeof sdkAdsConfig !== "undefined";

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta name="google-play-app" content="app-id=com.ps.bt" />
        <meta name="apple-itunes-app" content="app-id=531275824" />
        {sdkAdsEnabled ? (
          <meta name="bt-git-commit" content={__GIT_COMMIT__} />
        ) : null}
        <link rel="manifest" href="/web2-assets/manifest.json" />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500&family=Noto+Serif+SC:wght@400;500;600;800&display=swap"
          rel="stylesheet"
        />
        <Favicon />

        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: `
            (function() {
                /** CONFIGURATION START **/
                window._sf_async_config = window._sf_async_config || {};
                window._sf_async_config.uid = 52075;
                window._sf_async_config.domain = 'businesstimes.com.sg';
                window._sf_async_config.flickerControl = false;
                window._sf_async_config.useCanonical = true;
                window._sf_async_config.useCanonicalDomain = false;

                /** TELL CHARTBEAT TO AUTODETECT YOUTUBE VIDEOS **/
                window._cbv = window._cbv || [];
                window._cbv.push(["autoDetectYouTubeIframes"]);
                /** CONFIGURATION END **/
            })();
          `,
          }}
        />

        <script src="//static.chartbeat.com/js/chartbeat_mab.js" async></script>

        {/* Initialize the google variables. */}
        {/* Add a place to store the slot name variable. */}
        {sdkAdsEnabled ? null : (
          <script
            suppressHydrationWarning
            dangerouslySetInnerHTML={{
              __html: `
                var googletag = googletag || {};
                googletag.cmd = googletag.cmd || [];
                googletag.slots = googletag.slots || {};
              `,
            }}
          />
        )}

        {/* New relic script. */}
        {newRelicConfigScript && newRelicAgentUrl ? (
          <>
            <script
              dangerouslySetInnerHTML={{ __html: newRelicConfigScript }}
            />
            <script type="text/javascript" src={newRelicAgentUrl} async />
          </>
        ) : null}

        {sdkAdsEnabled ? null : (
          <script
            type="text/javascript"
            src="https://cdn.sphlabs.com/suid/suid.min.js"
          />
        )}

        <ConfigurationSettings />

        <Meta />

        <Links />

        <USFOrgSchema
          name="The Business Times"
          description="The Business Times - Singapore's leading business and financial news source. Find latest business news, market analysis, and economic insights from Singapore and Asia-Pacific."
          url={`${URL_ORIGIN}/`}
          logo={{
            url: "https://www.businesstimes.com.sg/assets-web2/logo-masthead-PNobukBu.svg",
            width: 475,
            height: 48,
          }}
          sameAs={[
            "https://www.facebook.com/thebusinesstimes",
            "https://twitter.com/businesstimes",
            "https://www.linkedin.com/showcase/the-business-times",
          ]}
          contactPoint={{
            telephone: "+65 63883838",
            contactType: "Customer Service",
            availableLanguage: "English",
            areaServed: { name: "Singapore" },
          }}
        />
      </head>

      <body>
        {sdkAdsEnabled ? null : <AdvertisementOverlay />}
        <div id="postitial_holder"></div>

        {children}
        <Scripts />

        <div id="mySPHLightbox"></div>

        <ChartbeatBodyScript />
      </body>
    </html>
  );
};

// Main application component
export default function App() {
  // ScrollRestoration emits warnings during SSR as it uses useLayoutEffect
  // Don't render that during SSR.
  const [render, setRender] = useState(false);

  const { pathname } = useLocation();
  const sdkAdsConfig = useBtSdkAdsCanaryConfigContext();
  const sdkAdsEnabled = typeof sdkAdsConfig !== "undefined";

  useAllCustomHooks({ legacyAdsEnabled: !sdkAdsEnabled, sdkAdsEnabled });

  useEffect(() => {
    setRender(true);
  }, []);

  const isCatfishAd = usePodcastPlayerStyle();

  const isWithAuth = !routesToExcludeFromLightbox.includes(pathname);

  return (
    <StrictMode>
      <CookiesProvider>
        <ProvidersRoot
          isWithAuth={isWithAuth}
          pathname={pathname}
          sdkAdsEnabled={sdkAdsEnabled}
        >
          <BtSdkAdsShell config={sdkAdsConfig}>
            <main
              className="flex w-screen max-w-full flex-col antialiased"
              id="background_container"
            >
              <div
                className="mx-auto flex w-full flex-grow flex-col items-center"
                id="content-container"
              >
                <Outlet />
              </div>
            </main>
          </BtSdkAdsShell>

          <StickyAudioPlayer
            mobilePlayerClassName={cn("z-[1000]", {
              "!bottom-[100px]": isCatfishAd,
            })}
          />
        </ProvidersRoot>
      </CookiesProvider>

      {/* ScrollRestoration emits warnings during client-side rendering */}
      {render ? <ScrollRestoration /> : null}

      {/* Disable Exit Intervention on MBO page */}
      {pathname !== RouteFactory.mbo ? (
        <CASBlock cdpId={CDP_EXIT_INTERVENTION_MODULE_ID} />
      ) : null}

      <PackageVersionTag />
      <PageGeneratedAtTag />
    </StrictMode>
  );
}

// Runs in the browser and catches errors that occur during rendering of React components on the client.
// I.e Catches that occur during CLIENT side routing
// Errors during rendering, lifecycle methods, and constructors in the React component tree.
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (isRouteErrorResponse(error)) {
    return (
      <>
        <h1>is Route Error Response</h1>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </>
    );
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </div>
    );
  } else {
    return <h1>Unknown Error</h1>;
  }
}
